import { Injectable } from "@angular/core";

/** Which set of route directions applies to the user: the bottom-strip layout or the side menu. */
export type NovaDevice = "mobile" | "desktop";

/**
 * The layout where the side navigation takes over from the bottom strip, written as the media query
 * the bottom nav itself hides on (`bottom-nav.component.scss` includes
 * `respond-to-any(tablet-landscape-touch, desktop)`; edges from `sass/_breakpoints.scss`: desktop is
 * width >= 1050px, and a touch tablet is 768px up to just under 1050px in landscape with no hover and
 * a coarse pointer). Keep the two in step.
 *
 * It is the LAYOUT that decides, not the device: a phone browser, the native app shell and a narrow
 * desktop window all show the bottom strip, and the route directions are written as "mobile: the
 * bottom strip" and "desktop: the side menu". `MobileService` only knows about the native shell, so
 * it would call a phone's browser desktop.
 */
const SIDE_NAV_LAYOUT =
  "(min-width: 1050px), (min-width: 768px) and (max-width: 1049.98px) and (orientation: landscape) and (hover: none) and (pointer: coarse)";

/**
 * Tells Nova which route directions to give. Read when a request is made rather than once, so a
 * window resized or a tablet turned mid-conversation is answered for the layout it has now.
 */
@Injectable({ providedIn: "root" })
export class NovaDeviceService {
  /** "desktop" when the side menu is showing, "mobile" when the bottom strip is, null when unknown. */
  current(): NovaDevice | null {
    try {
      return window.matchMedia(SIDE_NAV_LAYOUT).matches ? "desktop" : "mobile";
    } catch {
      // No matchMedia (an unusual embed, a test runner): unknown, and the server gives both.
      return null;
    }
  }
}
